import {
  formatDateTimeWithoutYear,
  formatDateWithoutYear,
  getRelativeTime,
} from "../../common/DateHelpers";

export const fmtRelativeTime = (value) => getRelativeTime(value);
export const fmtHoverDateTime = (value) => formatDateTimeWithoutYear(value);
export const fmtHoverDate = (value) => formatDateWithoutYear(value);

export const fmtOrderRupee = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return `₹${num.toLocaleString("en-IN")}`;
};

export const fmtInvoiceRupee = (value) => {
  const num = Number(value);

  if (Number.isNaN(num)) return "—";

  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const PAYMENT_ADDABLE_ORDER_STATUSES = new Set([
  "placed",
  "accepted",
  "dispatched",
]);

/** 24-hour labels at 30-minute steps: 00:00 … 23:30 */
export const PAYMENT_TIME_OPTIONS = (() => {
  const options = [];
  for (let hour = 0; hour < 24; hour += 1) {
    for (const minute of [0, 30]) {
      options.push(
        `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
      );
    }
  }
  return options;
})();

export const defaultPaymentTimeOption = () => {
  const now = new Date();
  const totalMinutes = now.getHours() * 60 + now.getMinutes();
  const slotMinutes = Math.floor(totalMinutes / 30) * 30;
  const hour = Math.floor(slotMinutes / 60);
  const minute = slotMinutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

export const defaultPaymentDateInputValue = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const combinePaymentDateAndTime = (dateValue, timeValue) => {
  if (!dateValue || !timeValue) return null;
  return `${dateValue}T${timeValue}:00`;
};

export const formatPaymentDateTime = (iso) => {
  if (iso == null || iso === "") return "—";
  const parsed = new Date(String(iso));
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

export const canAddPaymentsInSalesModal = (mode, status) => {
  const m = String(mode || "")
    .trim()
    .toLowerCase();
  const s = String(status || "")
    .trim()
    .toLowerCase();
  if (PAYMENT_ADDABLE_ORDER_STATUSES.has(s)) return true;
  if (m !== "direct" && s === "dealer_accepted") return true;

  return false;
};

export const formatStoreProjectStatusWord = (status) => {
  const s = String(status || "").trim();
  if (!s) return "—";
  if (s === "dealer_accepted") return "Dealer accepted";
  if (s === "rejected") return "Rejected";
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

/** Earliest shipment delivery-related timestamp for orders table sort. */
export const orderDeliverySortTimestamp = (row) => {
  const shipments = ensureArray(row?.shipments);
  let earliest = null;

  shipments.forEach((shipment) => {
    const status = String(shipment?.status || "").trim();
    let dateRaw = null;
    if (status === "draft") dateRaw = shipment?.delivery_date;
    else if (status === "dispatched") dateRaw = shipment?.dispatched_at;
    else if (status === "delivered") dateRaw = shipment?.delivered_at;

    const ts = tsOrZero(dateRaw);
    if (ts <= 0) return;
    if (earliest == null || ts < earliest) earliest = ts;
  });

  return earliest ?? 0;
};

/** Dealer dashboard order card status lines. */
export const formatDealerOrderStatusDisplay = (status) => {
  const s = String(status || "")
    .trim()
    .toLowerCase();
  if (s === "dealer_accepted") {
    return {
      primary: "Accepted by you",
      secondary: "Order not placed with the company yet",
    };
  }
  if (s === "accepted") {
    return { primary: "In Production", secondary: null };
  }
  return {
    primary: formatStoreProjectStatusWord(status),
    secondary: null,
  };
};
/** Order/project label: name + ZB id when assigned (after accept). */
export const formatStoreOrderLabel = (row) => {
  const name = String(row?.name || "").trim() || "Untitled";
  const orderId = String(row?.order_identifier || "").trim();
  return orderId ? `${name} (${orderId})` : name;
};

export const ensureArray = (value) => (Array.isArray(value) ? value : []);
export const ensureObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

/** Sales dashboard filter controls: light background, dark label text. */
export const STORE_DASHBOARD_FILTER_INPUT_CLASS = "unmask bg-light text-dark";
export const STORE_DASHBOARD_FILTER_TOGGLE_CLASS =
  "btn-light border text-dark text-start text-truncate unmask";

export const BUYER_ENGAGEMENT_STATUS_OPTIONS = [
  "new",
  "not_picking",
  "not_reachable",
  "connected",
  "pitched",
  "interested",
  "committed_next_month",
  "committed_this_month",
  "sample_quote",
  "actual_quote",
  "sample_panel",
  "cold",
  "rejected",
  "lost",
];

export const STORE_BUYER_FIT_VALUES = ["unknown", "fit", "not_fit"];

export const STORE_BUYER_FIT_FILTER_OPTIONS = [
  { value: "unknown", label: "Unknown" },
  { value: "fit", label: "Fit" },
  { value: "not_fit", label: "Not Fit" },
];

export const STORE_BUYER_CONVICTION_VALUES = ["no", "may_be", "yes"];

export const STORE_BUYER_CONVICTION_FILTER_OPTIONS = [
  { value: "no", label: "No" },
  { value: "may_be", label: "Maybe" },
  { value: "yes", label: "Yes" },
];

export const convictionRowFilterValue = (conviction) => conviction;

const COMMITTED_DATE_ANCHOR_DAYS = [5, 10, 15, 20, 25];
const COMMITTED_DATE_WINDOW_DAYS = 30;

const pad2 = (n) => String(n).padStart(2, "0");

export const formatCommittedDateIso = (date) => {
  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();

  return `${y}-${pad2(m)}-${pad2(d)}`;
};

const startOfLocalDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addLocalDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);

  return next;
};

const endOfLocalMonth = (year, monthIndex) =>
  new Date(year, monthIndex + 1, 0);

const isCommittedDateAnchorDay = (date) => {
  const day = date.getDate();
  if (COMMITTED_DATE_ANCHOR_DAYS.includes(day)) return true;

  return day === endOfLocalMonth(date.getFullYear(), date.getMonth()).getDate();
};

const committedDateLabel = (iso) => {
  const [y, m, d] = String(iso).split("-").map((part) => Number(part));
  const date = new Date(y, m - 1, d);

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const committedDatePickerOptions = (fromDate = new Date(), existingIso = "") => {
  const start = startOfLocalDay(fromDate);
  const end = addLocalDays(start, COMMITTED_DATE_WINDOW_DAYS);
  const values = new Set();

  let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cursor <= end) {
    const year = cursor.getFullYear();
    const monthIndex = cursor.getMonth();
    const monthEnd = endOfLocalMonth(year, monthIndex);

    COMMITTED_DATE_ANCHOR_DAYS.forEach((day) => {
      const candidate = new Date(year, monthIndex, day);
      if (candidate >= start && candidate <= end) {
        values.add(formatCommittedDateIso(candidate));
      }
    });

    if (monthEnd >= start && monthEnd <= end) {
      values.add(formatCommittedDateIso(monthEnd));
    }

    cursor = new Date(year, monthIndex + 1, 1);
  }

  const existing = String(existingIso || "").trim();
  if (existing) values.add(existing);

  const sorted = Array.from(values).sort((a, b) => a.localeCompare(b));

  return [
    { value: "", label: "—" },
    ...sorted.map((value) => ({ value, label: committedDateLabel(value) })),
  ];
};

export const COMMITTED_DATE_FILTER_NONE = "__none__";

export const committedDateRowFilterValue = (committedDate) => {
  const iso = String(committedDate || "").trim();
  if (!iso) return COMMITTED_DATE_FILTER_NONE;

  return iso;
};

export const buildCommittedDateFilterOptions = (rows) => {
  const values = new Set();
  let hasNone = false;

  ensureArray(rows).forEach((row) => {
    const filterValue =
      row?.committedDateFilterValue != null
        ? row.committedDateFilterValue
        : committedDateRowFilterValue(row?.committedDate);

    if (filterValue === COMMITTED_DATE_FILTER_NONE) {
      hasNone = true;
    } else {
      values.add(filterValue);
    }
  });

  const options = [];
  if (hasNone) {
    options.push({ value: COMMITTED_DATE_FILTER_NONE, label: "—" });
  }

  Array.from(values)
    .sort((a, b) => a.localeCompare(b))
    .forEach((value) => {
      options.push({ value, label: committedDateLabel(value) });
    });

  return options;
};

const COMMITTED_DATE_FILTER_ISO_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const parseCommittedDateFilterFromUrl = (raw) =>
  parseCommaSeparatedUrlList(raw).filter(
    (value) =>
      value === COMMITTED_DATE_FILTER_NONE ||
      COMMITTED_DATE_FILTER_ISO_PATTERN.test(value)
  );

export const buyerHasAcceptedOrder = (buyer) =>
  Boolean(buyer?.has_accepted_order);

export const CONTACT_SUB_STATUS_NONE = "contact_none";
export const CONTACT_SUB_STATUS_MARKETED = "contact_marketed";
export const CONTACT_SUB_STATUS_ENQUIRED = "contact_enquired";
export const CONTACT_SUB_STATUS_REJECTED = "contact_rejected";
export const CONTACT_SUB_STATUS_NOT_INTERESTED = "contact_not_interested";

export const formatContactImportStats = (stats) => {
  const rows = Number(stats?.rows) || 0;
  const created = Number(stats?.created) || 0;
  const skipped = Number(stats?.skipped) || 0;
  return `Processed: ${rows}. Imported: ${created}. Skipped: ${skipped}`;
};

export const formatContactStatusLabel = (subStatus) => {
  const sub = String(subStatus || "").trim();
  if (sub === CONTACT_SUB_STATUS_MARKETED) return "Marketed";
  if (sub === CONTACT_SUB_STATUS_ENQUIRED) return "Enquired";
  if (sub === CONTACT_SUB_STATUS_REJECTED) return "Rejected";
  if (sub === CONTACT_SUB_STATUS_NOT_INTERESTED) return "Not Interested";
  return "-";
};

export const toErrorText = (err, fallback) => {
  const candidate = err?.error ?? err?.message ?? fallback;
  if (typeof candidate === "string") return candidate;
  if (candidate && typeof candidate === "object") {
    if (typeof candidate.message === "string") return candidate.message;
    if (typeof candidate.code === "string") return candidate.code;
    try {
      return JSON.stringify(candidate);
    } catch {
      return fallback;
    }
  }
  return fallback;
};

export const metaToKeyValueLines = (meta) => {
  const obj = ensureObject(meta);
  const keys = Object.keys(obj).filter((k) => {
    const v = obj[k];
    return v !== null && typeof v !== "undefined";
  });
  if (keys.length === 0) return [];
  keys.sort((a, b) => String(a).localeCompare(String(b)));
  return keys.map((k) => {
    const v = obj[k];
    let vText = "";
    if (typeof v === "string") vText = v;
    else if (typeof v === "number" || typeof v === "boolean") vText = String(v);
    else {
      try {
        vText = JSON.stringify(v);
      } catch {
        vText = String(v);
      }
    }
    return { key: String(k), value: vText };
  });
};

export const uniqBroadcastTemplateOptions = (broadcasts) => {
  const ids = Array.from(
    new Set(
      ensureArray(broadcasts)
        .map((b) => String(b?.template ?? "").trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b));

  return ids.map((id) => ({ value: id, label: id }));
};

/** First buyer user (created_at), preferring non-temp — matches dealer dashboard payload. */
export const firstStoreUserForBuyer = (users) => {
  const list = ensureArray(users)
    .slice()
    .sort((a, b) => {
      const aTs = new Date(a?.created_at || 0).getTime();
      const bTs = new Date(b?.created_at || 0).getTime();
      if (aTs !== bTs) return aTs - bTs;
      return Number(a?.id || 0) - Number(b?.id || 0);
    });
  return list.find((u) => !u?.temp) || list[0] || null;
};

export const formatBuyerFirstUserLine = (firstUser) => {
  if (!firstUser) return "—";
  const name = String(firstUser?.name || "").trim();
  const mobile = String(firstUser?.mobile || "").trim();
  if (name && mobile) return `${name} / ${mobile}`;
  return name || mobile || "—";
};

export const buyerDashboardNotificationName = (row) => {
  const company = String(row?.buyerName || "").trim();
  if (company) return company;
  const mobile = String(row?.firstUserMobile || "").trim();
  if (mobile && mobile !== "-") return mobile;
  return String(row?.buyerDisplayName || row?.name || "").trim();
};

export const primaryStoreUserIdForRow = (row) => {
  const first = firstStoreUserForBuyer(row?.raw?.users);
  return first?.id ?? null;
};

const normalizeStoreUserId = (id) => {
  if (id == null || id === "") return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
};

export const buildSentTemplatesByUserId = (notifications) => {
  const map = new Map();
  ensureArray(notifications).forEach((n) => {
    const uid = normalizeStoreUserId(n?.store_user_id ?? n?.storeUserId);
    if (uid == null) return;
    const template = String(n?.template_name ?? n?.templateName ?? "").trim();
    if (!template || template === "-") return;
    if (!map.has(uid)) map.set(uid, new Set());
    map.get(uid).add(template);
  });
  return map;
};

export const userPassesEventFilters = (
  userId,
  sentTemplatesByUserId,
  eventSent,
  eventNotSent
) => {
  const uid = normalizeStoreUserId(userId);
  if (uid == null) return false;
  const sent = sentTemplatesByUserId.get(uid) || new Set();
  const sentFilters = ensureArray(eventSent);
  const notSentFilters = ensureArray(eventNotSent);
  if (sentFilters.length > 0 && !sentFilters.some((t) => sent.has(t))) {
    return false;
  }
  if (notSentFilters.length > 0 && notSentFilters.some((t) => sent.has(t))) {
    return false;
  }
  return true;
};

export const rowPassesEventFilters = (
  row,
  sentTemplatesByUserId,
  eventSent,
  eventNotSent
) => {
  const users = ensureArray(row?.raw?.users);
  if (users.length === 0) return false;
  const sentFilters = ensureArray(eventSent);
  const notSentFilters = ensureArray(eventNotSent);
  if (sentFilters.length === 0 && notSentFilters.length === 0) return true;
  return users.some((u) =>
    userPassesEventFilters(
      u?.id ?? u?.store_user_id,
      sentTemplatesByUserId,
      eventSent,
      eventNotSent
    )
  );
};

export const BUYER_CITY_FILTER_TABS = [
  "prospects",
  "customers",
  "leads",
  "contacts",
];

export const BUYER_STATE_FILTER_TABS = BUYER_CITY_FILTER_TABS;

const BUYER_PROSPECT_STYLE_FILTER_TABS = ["prospects", "customers"];

export const PRIORITY_BUYER_CITY_OPTIONS = [
  "Chennai",
  "Bengaluru",
  "Hyderabad",
  "Secundarabad",
];

export const PRIORITY_BUYER_STATE_OPTIONS = [
  "Tamil Nadu",
  "Karnataka",
  "Telangana",
  "Andhra Pradesh"
];

export const orderBuyerCityOptions = (cities, { includePriority = true } = {}) => {
  const fromData = new Set(
    ensureArray(cities)
      .map((city) => String(city).trim())
      .filter((city) => city && city !== "-")
  );
  if (includePriority) {
    PRIORITY_BUYER_CITY_OPTIONS.forEach((city) => fromData.add(city));
  }
  const priority = includePriority
    ? PRIORITY_BUYER_CITY_OPTIONS
    : PRIORITY_BUYER_CITY_OPTIONS.filter((city) => fromData.has(city));
  const rest = Array.from(fromData)
    .filter((city) => !priority.includes(city))
    .sort((a, b) => a.localeCompare(b));

  return [...priority, ...rest];
};

export const buildBuyerCityOptionsFromRows = (rows, stateFilter = []) => {
  const selectedStates = ensureArray(stateFilter)
    .map((state) => String(state).trim())
    .filter((state) => state && state !== "-");
  const cities = new Set();

  ensureArray(rows).forEach((row) => {
    const stateLabel = String(row.stateLabel || "").trim();
    if (selectedStates.length > 0 && !selectedStates.includes(stateLabel)) return;

    const cityLabel = String(row.cityLabel || "").trim();
    if (cityLabel && cityLabel !== "-") cities.add(cityLabel);
  });

  const includePriority = selectedStates.length === 0;

  return orderBuyerCityOptions(Array.from(cities), { includePriority });
};

export const orderBuyerStateOptions = (states) => {
  const fromData = new Set(
    ensureArray(states)
      .map((state) => String(state).trim())
      .filter((state) => state && state !== "-")
  );
  PRIORITY_BUYER_STATE_OPTIONS.forEach((state) => fromData.add(state));
  const rest = Array.from(fromData)
    .filter((state) => !PRIORITY_BUYER_STATE_OPTIONS.includes(state))
    .sort((a, b) => a.localeCompare(b));

  return [...PRIORITY_BUYER_STATE_OPTIONS, ...rest];
};

/** Tab-scoped list filters for the store sales dashboard buyer tables. */
export const applyBuyerTabFilters = ({
  rows,
  tab,
  searchText = "",
  buyerStateFilter = [],
  buyerCityFilter = [],
  buyerPpmFilter = [],
  buyerEngagementFilter = [],
  buyerSalesOwnerFilter = [],
  buyerProfileTypeFilter = [],
  eventSentFilter = [],
  eventNotSentFilter = [],
  sentTemplatesByUserId = {},
  buyerVisibilityFilter = "all",
  buyerContactStatusFilter = "",
  buyerSourceFilter = "",
  profileFitFilter = [],
  convictionFilter = [],
  committedDateFilter = [],
  buyerAcquiredMonthFilter = [],
}) => {
  let next = [...ensureArray(rows)];

  const query = String(searchText || "").trim().toLowerCase();
  if (query) {
    next = next.filter(
      (r) =>
        String(r.buyerName || "").toLowerCase().includes(query) ||
        String(r.firstUserName || "").toLowerCase().includes(query) ||
        String(r.firstUserMobile || "").toLowerCase().includes(query) ||
        String(r.id).includes(query)
    );
  }

  if (
    BUYER_STATE_FILTER_TABS.includes(tab) &&
    ensureArray(buyerStateFilter).length > 0
  ) {
    next = next.filter((r) =>
      ensureArray(buyerStateFilter).includes(String(r.stateLabel || "").trim())
    );
  }

  if (
    BUYER_CITY_FILTER_TABS.includes(tab) &&
    ensureArray(buyerCityFilter).length > 0
  ) {
    next = next.filter((r) =>
      ensureArray(buyerCityFilter).includes(String(r.cityLabel || "").trim())
    );
  }

  if (
    BUYER_PROSPECT_STYLE_FILTER_TABS.includes(tab) &&
    ensureArray(buyerPpmFilter).length > 0
  ) {
    next = next.filter((r) => {
      if (buyerPpmFilter.includes("__none__") && r.projectsPerMonth == null) {
        return true;
      }
      const ppm = Number(r.projectsPerMonth);
      return buyerPpmFilter.some(
        (v) => v !== "__none__" && Number(v) === ppm
      );
    });
  }

  if (
    BUYER_PROSPECT_STYLE_FILTER_TABS.includes(tab) &&
    ensureArray(buyerEngagementFilter).length > 0
  ) {
    next = next.filter((r) =>
      buyerEngagementFilter.includes(
        String(r.raw?.engagement_status || "").trim()
      )
    );
  }

  if (
    BUYER_PROSPECT_STYLE_FILTER_TABS.includes(tab) &&
    ensureArray(buyerSalesOwnerFilter).length > 0
  ) {
    next = next.filter((r) => {
      if (buyerSalesOwnerFilter.includes("__none__") && r.salesOwnerId == null) {
        return true;
      }
      const ownerId = r.salesOwnerId == null ? null : Number(r.salesOwnerId);
      return buyerSalesOwnerFilter.some(
        (v) => v !== "__none__" && Number(v) === ownerId
      );
    });
  }

  if (
    BUYER_PROSPECT_STYLE_FILTER_TABS.includes(tab) &&
    ensureArray(buyerProfileTypeFilter).length > 0
  ) {
    next = next.filter((r) =>
      buyerProfileTypeFilter.includes(String(r.profileType || "").trim())
    );
  }

  const hasEventFilters =
    ensureArray(eventSentFilter).length > 0 ||
    ensureArray(eventNotSentFilter).length > 0;
  if (
    hasEventFilters &&
    (tab === "customers" || tab === "prospects" || tab === "leads")
  ) {
    next = next.filter((r) =>
      rowPassesEventFilters(
        r,
        sentTemplatesByUserId,
        eventSentFilter,
        eventNotSentFilter
      )
    );
  }

  if (tab === "customers") {
    next = next.filter(
      (r) => String(r.customerStatus || "").trim() === "active"
    );

    if (ensureArray(buyerAcquiredMonthFilter).length > 0) {
      next = next.filter((r) => {
        if (buyerAcquiredMonthFilter.includes("__none__") && !r.acquiredAt) {
          return true;
        }
        const monthKey = buyerAcquiredMonthKey(r.acquiredAt);
        return buyerAcquiredMonthFilter.some(
          (v) => v !== "__none__" && v === monthKey
        );
      });
    }
  } else if (tab === "prospects" && buyerVisibilityFilter !== "all") {
    next = next.filter((r) => {
      const customerStatus = String(r.customerStatus || "pending").trim();
      const isSampleCustomer = customerStatus === "sample";
      const isPendingCustomer =
        String(r.status || "").trim() === "customer" &&
        customerStatus === "pending";

      if (buyerVisibilityFilter === "sample") {
        return isSampleCustomer;
      }

      const status = String(r.status || "").trim();
      const sub = String(r.subStatus || "").trim();
      const isIgnored =
        (status === "prospect" && sub.length > 0) || status === "archived";

      if (buyerVisibilityFilter === "ignored") {
        return isIgnored && !isSampleCustomer;
      }

      return (!isIgnored && !isSampleCustomer) || isPendingCustomer;
    });
  }

  if (tab === "contacts") {
    if (buyerContactStatusFilter) {
      next = next.filter(
        (r) => String(r.subStatus || "").trim() === buyerContactStatusFilter
      );
    }
    if (buyerSourceFilter) {
      next = next.filter(
        (r) => String(r.leadSource || "").trim() === buyerSourceFilter
      );
    }
  }

  if (tab === "prospects" && ensureArray(profileFitFilter).length > 0) {
    next = next.filter((r) => profileFitFilter.includes(r.profileFit));
  }

  if (tab === "prospects" && ensureArray(convictionFilter).length > 0) {
    next = next.filter((r) =>
      convictionFilter.includes(r.convictionFilterValue)
    );
  }

  if (tab === "prospects" && ensureArray(committedDateFilter).length > 0) {
    next = next.filter((r) =>
      committedDateFilter.includes(r.committedDateFilterValue)
    );
  }

  return next;
};

export const resolveSalesOwnerLabel = (buyer, adminUsersById = null) => {
  const fromApi = String(buyer?.sales_owner_name || "").trim();
  if (fromApi) return fromApi;
  const ownerId = buyer?.sales_owner_id;
  if (ownerId == null || ownerId === "") return "—";
  const admin =
    adminUsersById?.get?.(Number(ownerId)) ??
    ensureArray(adminUsersById).find((u) => Number(u?.id) === Number(ownerId));
  if (admin) {
    const label =
      String(admin?.name || "").trim() || String(admin?.mobile || "").trim();
    if (label) return label;
  }
  return "—";
};

export const mapBuyersToRows = (buyers, adminUsers = []) => {
  const adminUsersById = new Map(
    ensureArray(adminUsers)
      .filter((u) => u?.id != null)
      .map((u) => [Number(u.id), u])
  );
  return ensureArray(buyers)
    .map((buyer) => {
      const users = ensureArray(buyer?.users);
      const firstUser = firstStoreUserForBuyer(users);
      const buyerName = String(buyer?.name || "").trim();
      const firstUserMobile = String(firstUser?.mobile || "").trim();
      const firstUserName = String(firstUser?.name || "").trim();
      const buyerCity = String(buyer?.city || "").trim();
      const buyerState = String(buyer?.state || "").trim();
      const subStatus = String(buyer?.sub_status || "").trim();
      const engagementStatus = String(buyer?.engagement_status || "new").trim();
      const latestSalesNoteBody = String(
        buyer?.latest_sales_note_body || ""
      ).trim();
      const salesNotes = ensureArray(buyer?.sales_notes);
      const salesOwnerIdRaw = buyer?.sales_owner_id;
      const salesOwnerId =
        salesOwnerIdRaw == null || salesOwnerIdRaw === ""
          ? null
          : Number(salesOwnerIdRaw);
      const hasAcceptedOrder = buyerHasAcceptedOrder(buyer);
      const customerStatus = String(buyer?.customer_status || "pending").trim();
      const projectsPerMonthRaw = buyer?.projects_per_month;
      const projectsPerMonth =
        projectsPerMonthRaw == null || projectsPerMonthRaw === ""
          ? null
          : Number(projectsPerMonthRaw);
      return {
        id: buyer?.id ?? null,
        name: buyerName || "—",
        buyerName: buyerName || "",
        buyerDisplayName: buyerName || "—",
        firstUserSubtitle: formatBuyerFirstUserLine(firstUser),
        hasBuyerName: Boolean(buyerName),
        profileType: String(buyer?.profile_type || "").trim() || "-",
        projectsPerMonth,
        engagementStatus: engagementStatus || "new",
        engRemarks: latestSalesNoteBody || "-",
        latestSalesNoteBody: latestSalesNoteBody || "-",
        salesNotes,
        salesOwnerId,
        salesOwnerName: resolveSalesOwnerLabel(buyer, adminUsersById),
        lastFollowupAt: buyer?.last_followup_at || null,
        followupDate: buyer?.followup_date || null,
        hasAcceptedOrder,
        customerStatus,
        subStatus: subStatus || "",
        contactStatusLabel: formatContactStatusLabel(subStatus),
        city: buyerCity || "-",
        cityLabel: buyerCity || "-",
        state: buyerState || "-",
        stateLabel: buyerState || "-",
        status: String(buyer?.status || "prospect"),
        dealerName: String(buyer?.dealer_name || "-"),
        leadSource: String(buyer?.lead_source || "-"),
        firstUserName: firstUserName || "—",
        firstUserMobile: firstUserMobile || "—",
        signedUpAt: buyer?.signed_up_at || null,
        createdAt: buyer?.created_at || null,
        acquiredAt: buyer?.acquired_at || null,
        sampleAcceptedAt: buyer?.sample_accepted_at || null,
        lastActivityAt: buyer?.last_activity_at || null,
        sessionsCount: Number(buyer?.sessions_count || 0) || 0,
        signupStartCount: Number(buyer?.signup_start_count || 0) || 0,
        activitiesCount: Number(buyer?.activities_count || 0) || 0,
        projectsCount: Number(buyer?.projects_count || 0) || 0,
        hasSampleProject: buyer?.has_sample_project === true,
        profileFit: buyer.fit,
        conviction: buyer.conviction,
        convictionFilterValue: convictionRowFilterValue(buyer.conviction),
        committedDate: buyer.committed_date || null,
        committedDateFilterValue: committedDateRowFilterValue(
          buyer.committed_date
        ),
        raw: buyer || {},
      };
    })
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

const BUYER_EDIT_DASHBOARD_FIELDS = [
  "id",
  "name",
  "city",
  "profile_type",
  "fit",
  "awareness",
  "status",
  "sub_status",
  "engagement_status",
  "projects_per_month",
  "last_followup_at",
  "followup_date",
  "conviction",
  "committed_date",
  "sales_owner_id",
  "sales_owner_name",
  "latest_sales_note_body",
  "sales_notes",
];

export const dashboardListForBuyerStatus = (status) => {
  const normalized = String(status || "").trim();
  if (normalized === "contact") return "contacts";
  if (normalized === "lead") return "leads";

  return "buyers";
};

const patchPrimaryBuyerUserName = (users, userName) => {
  if (!Array.isArray(users)) return users;

  const firstUser = firstStoreUserForBuyer(users);
  if (!firstUser) return users;

  return users.map((user) =>
    Number(user?.id) === Number(firstUser.id) ? { ...user, name: userName } : user
  );
};

export const mergeEditedBuyerIntoDashboardRow = (existing, edited) => {
  if (!edited) return existing || null;
  if (!existing) return { ...edited };

  const merged = { ...existing };
  BUYER_EDIT_DASHBOARD_FIELDS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(edited, key)) {
      merged[key] = edited[key];
    }
  });

  if (Object.prototype.hasOwnProperty.call(edited, "buyer_user_name")) {
    if (Array.isArray(merged.users)) {
      merged.users = patchPrimaryBuyerUserName(
        merged.users,
        edited.buyer_user_name
      );
    }

    const rawBuyer = merged.raw;
    if (rawBuyer && Array.isArray(rawBuyer.users)) {
      const nextUsers = patchPrimaryBuyerUserName(
        rawBuyer.users,
        edited.buyer_user_name
      );
      const userName = String(edited.buyer_user_name || "").trim();
      merged.firstUserName = userName || "—";
      merged.raw = { ...rawBuyer, users: nextUsers };
      merged.firstUserSubtitle = formatBuyerFirstUserLine(
        firstStoreUserForBuyer(nextUsers)
      );
    }
  }

  return merged;
};

const replaceBuyerInList = (list, buyerId, mergedBuyer) => {
  const id = Number(buyerId);
  const index = ensureArray(list).findIndex((row) => Number(row?.id) === id);
  if (index < 0) return [...ensureArray(list), mergedBuyer];

  const next = [...list];
  next[index] = mergedBuyer;

  return next;
};

const removeBuyerFromList = (list, buyerId) =>
  ensureArray(list).filter((row) => Number(row?.id) !== Number(buyerId));

export const patchBuyerDashboardLists = (
  buyers,
  leads,
  contacts,
  buyerId,
  editedBuyer
) => {
  const id = Number(buyerId);
  let existing = null;
  let sourceList = null;

  const buyerIndex = ensureArray(buyers).findIndex((row) => Number(row?.id) === id);
  if (buyerIndex >= 0) {
    existing = buyers[buyerIndex];
    sourceList = "buyers";
  } else {
    const leadIndex = ensureArray(leads).findIndex((row) => Number(row?.id) === id);
    if (leadIndex >= 0) {
      existing = leads[leadIndex];
      sourceList = "leads";
    } else {
      const contactIndex = ensureArray(contacts).findIndex(
        (row) => Number(row?.id) === id
      );
      if (contactIndex >= 0) {
        existing = contacts[contactIndex];
        sourceList = "contacts";
      }
    }
  }

  const mergedBuyer = mergeEditedBuyerIntoDashboardRow(existing, editedBuyer);
  const targetList = dashboardListForBuyerStatus(mergedBuyer?.status);

  let nextBuyers = buyers;
  let nextLeads = leads;
  let nextContacts = contacts;

  if (sourceList === targetList) {
    if (targetList === "buyers") {
      nextBuyers = replaceBuyerInList(buyers, id, mergedBuyer);
    } else if (targetList === "leads") {
      nextLeads = replaceBuyerInList(leads, id, mergedBuyer);
    } else if (targetList === "contacts") {
      nextContacts = replaceBuyerInList(contacts, id, mergedBuyer);
    }

    return {
      buyers: nextBuyers,
      leads: nextLeads,
      contacts: nextContacts,
      mergedBuyer,
    };
  }

  if (sourceList === "buyers") nextBuyers = removeBuyerFromList(buyers, id);
  else if (sourceList === "leads") nextLeads = removeBuyerFromList(leads, id);
  else if (sourceList === "contacts") {
    nextContacts = removeBuyerFromList(contacts, id);
  }

  if (targetList === "buyers") {
    nextBuyers = replaceBuyerInList(nextBuyers, id, mergedBuyer);
  } else if (targetList === "leads") {
    nextLeads = replaceBuyerInList(nextLeads, id, mergedBuyer);
  } else if (targetList === "contacts") {
    nextContacts = replaceBuyerInList(nextContacts, id, mergedBuyer);
  }

  return {
    buyers: nextBuyers,
    leads: nextLeads,
    contacts: nextContacts,
    mergedBuyer,
  };
};

export const sortRows = (rows, sortConfig) => {
  const sorted = [...rows];
  const { key, direction } = sortConfig;
  const factor = direction === "asc" ? 1 : -1;

  sorted.sort((a, b) => {
    if (key === "name") {
      return (
        String(a.buyerDisplayName || a.name || "").localeCompare(
          String(b.buyerDisplayName || b.name || "")
        ) * factor
      );
    }
    if (
      key === "status" ||
      key === "leadSource" ||
      key === "city" ||
      key === "profileType" ||
      key === "dealerName" ||
      key === "engagementStatus" ||
      key === "contactStatusLabel"
    ) {
      const aValue = key === "city" ? a.cityLabel : a[key];
      const bValue = key === "city" ? b.cityLabel : b[key];
      return String(aValue || "").localeCompare(String(bValue || "")) * factor;
    }
    if (
      key === "signedUpAt" ||
      key === "createdAt" ||
      key === "acquiredAt" ||
      key === "sampleAcceptedAt" ||
      key === "lastActivityAt" ||
      key === "lastFollowupAt" ||
      key === "followupDate"
    ) {
      const aTs = a[key] ? new Date(a[key]).getTime() : 0;
      const bTs = b[key] ? new Date(b[key]).getTime() : 0;
      return (aTs - bTs) * factor;
    }
    if (key === "sessionsCount" || key === "signupStartCount" || key === "projectsCount") {
      const aN = Number(a[key] || 0);
      const bN = Number(b[key] || 0);
      return (aN - bN) * factor;
    }
    if (key === "projectsPerMonth") {
      const aN = a.projectsPerMonth == null ? -1 : Number(a.projectsPerMonth);
      const bN = b.projectsPerMonth == null ? -1 : Number(b.projectsPerMonth);
      return (aN - bN) * factor;
    }
    if (key === "customerOrderTotal") {
      const aN = Number(a.customerOrderStats?.total?.value || 0);
      const bN = Number(b.customerOrderStats?.total?.value || 0);
      return (aN - bN) * factor;
    }
    if (key.startsWith("customerOrderMonth_")) {
      const monthKey = key.slice("customerOrderMonth_".length);
      const aN = Number(a.customerOrderStats?.months?.[monthKey]?.value || 0);
      const bN = Number(b.customerOrderStats?.months?.[monthKey]?.value || 0);
      return (aN - bN) * factor;
    }
    return 0;
  });

  return sorted;
};

export const tsOrZero = (value) => {
  if (!value) return 0;
  const ts = new Date(value).getTime();
  return Number.isNaN(ts) ? 0 : ts;
};

export const uniqById = (rows) => {
  const seen = new Set();
  return ensureArray(rows).filter((row) => {
    const id = row?.id;
    if (id == null) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

export const mapDealersToRows = (dealers) => {
  return ensureArray(dealers)
    .map((dealer) => {
      const firstDealerUser = ensureArray(dealer?.dealer_users)[0];
      return {
        id: dealer?.id ?? null,
        name: String(dealer?.name || "").trim() || "Unknown",
        mobile: String(firstDealerUser?.mobile || "").trim() || "-",
        signedUpAt: dealer?.created_at || null,
        buyers: ensureArray(dealer?.buyers),
        invites: ensureArray(dealer?.invites),
      };
    })
    .sort((a, b) => tsOrZero(b.signedUpAt) - tsOrZero(a.signedUpAt));
};

export const NOTIFICATION_SORT_DEFAULT = {
  key: "createdAt",
  direction: "desc",
};

export const CUSTOMER_ORDER_MONTH_START_KEY = "2026-05";

export const emptyCustomerOrderMonthBucket = () => ({ value: 0, count: 0 });

export const buyerAcquiredMonthKey = (acquiredAt) => {
  if (!acquiredAt) return null;
  const d = new Date(acquiredAt);
  if (Number.isNaN(d.getTime())) return null;

  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export const buildBuyerAcquiredMonthFilterOptions = (
  rows,
  asOfDate = new Date()
) => {
  const keys = new Set(buildCustomerOrderMonthKeys(asOfDate));
  ensureArray(rows).forEach((row) => {
    const key = buyerAcquiredMonthKey(row?.acquiredAt);
    if (key) keys.add(key);
  });

  return Array.from(keys)
    .sort((a, b) => b.localeCompare(a))
    .map((key) => ({
      value: key,
      label: formatCustomerOrderMonthHeader(key),
    }));
};

export const buildCustomerOrderMonthKeys = (asOfDate = new Date()) => {
  const keys = [];
  const [startYear, startMonth] = CUSTOMER_ORDER_MONTH_START_KEY.split("-").map(
    Number
  );
  const start = new Date(startYear, startMonth - 1, 1);
  const end = new Date(asOfDate.getFullYear(), asOfDate.getMonth(), 1);
  const cursor = new Date(start);

  while (cursor <= end) {
    keys.push(
      `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`
    );
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return keys;
};

export const formatCustomerOrderMonthHeader = (monthKey) => {
  const [year, month] = monthKey.split("-").map(Number);
  const date = new Date(year, month - 1, 1);
  const mon = date.toLocaleString("default", { month: "short" });
  const yy = String(year).slice(-2);

  return `${mon} '${yy}`;
};

export const isCustomerDashboardAcceptedOrder = (order) => {
  const status = String(order?.status || "").trim();
  if (!ORDER_ACCEPTED_DATE_STATUS_FILTERS.includes(status)) return false;
  if (!order?.accepted_at) return false;

  return true;
};

const orderAcceptedMonthKey = (order) => {
  const d = new Date(order.accepted_at);
  if (Number.isNaN(d.getTime())) return null;

  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export const buildBuyerOrderMonthStats = (orders, monthKeys) => {
  const byBuyerId = new Map();
  const monthTotals = Object.fromEntries(
    monthKeys.map((key) => [key, emptyCustomerOrderMonthBucket()])
  );
  const grandTotal = emptyCustomerOrderMonthBucket();

  const ensureBuyer = (buyerId) => {
    if (!byBuyerId.has(buyerId)) {
      const months = Object.fromEntries(
        monthKeys.map((key) => [key, emptyCustomerOrderMonthBucket()])
      );
      byBuyerId.set(buyerId, {
        total: emptyCustomerOrderMonthBucket(),
        months,
      });
    }

    return byBuyerId.get(buyerId);
  };

  const addToBucket = (bucket, value) => {
    bucket.value += value;
    bucket.count += 1;
  };

  ensureArray(orders).forEach((order) => {
    if (!isCustomerDashboardAcceptedOrder(order)) return;

    const buyerId = order?.buyer_id;
    if (buyerId == null) return;

    const monthKey = orderAcceptedMonthKey(order);
    if (!monthKey || !monthKeys.includes(monthKey)) return;

    const meta = order?.buyer_price_meta;
    const rawValue =
      meta && typeof meta === "object" ? Number(meta.total) : 0;
    const value = Number.isFinite(rawValue) ? rawValue : 0;
    const id = Number(buyerId);
    const buyerStats = ensureBuyer(id);

    addToBucket(buyerStats.months[monthKey], value);
    addToBucket(buyerStats.total, value);
    addToBucket(monthTotals[monthKey], value);
    addToBucket(grandTotal, value);
  });

  return { byBuyerId, monthTotals, grandTotal };
};

export const aggregateBuyerOrderMonthStatsForBuyerIds = (
  buyerOrderMonthStats,
  buyerIds,
  monthKeys
) => {
  const idSet = new Set(
    ensureArray(buyerIds)
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id))
  );
  const monthTotals = Object.fromEntries(
    ensureArray(monthKeys).map((key) => [key, emptyCustomerOrderMonthBucket()])
  );
  const grandTotal = emptyCustomerOrderMonthBucket();

  const addBuckets = (target, source) => {
    target.value += Number(source?.value || 0);
    target.count += Number(source?.count || 0);
  };

  idSet.forEach((id) => {
    const buyerStats = buyerOrderMonthStats?.byBuyerId?.get(id);
    if (!buyerStats) return;

    addBuckets(grandTotal, buyerStats.total);
    ensureArray(monthKeys).forEach((key) => {
      addBuckets(monthTotals[key], buyerStats.months?.[key]);
    });
  });

  return { monthTotals, grandTotal };
};

export const ORDER_MONTH_FILTER_OPTIONS = [
  { label: "All", value: "" },
  { label: "May 2026", value: "2026-05" },
  { label: "June 2026", value: "2026-06" },
  { label: "July 2026", value: "2026-07" },
];

export const matchesOrdersMonthFilter = (order, monthFilter) => {
  const filters = ensureArray(monthFilter);
  if (filters.length === 0) return true;
  const acceptedAt = order?.accepted_at;
  if (!acceptedAt) return false;
  const d = new Date(acceptedAt);
  if (Number.isNaN(d.getTime())) return false;
  const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  return filters.includes(key);
};

export const matchesOrdersStatusFilter = (order, statusFilter) => {
  const filters = ensureArray(statusFilter);
  if (filters.length === 0) return true;

  return filters.includes(order?.status);
};

export const ordersValueTotal = (orders) => {
  return ensureArray(orders).reduce((sum, row) => {
    const meta = row?.buyer_price_meta;
    if (!meta || typeof meta !== "object") return sum;
    const n = Number(meta.total);
    return Number.isFinite(n) ? sum + n : sum;
  }, 0);
};

export const ORDER_ACCEPTED_DATE_STATUS_FILTERS = [
  "accepted",
  "dispatched",
  "partially_delivered",
  "delivered",
];

export const ORDER_STATUS_OPTIONS = [
  { label: "Placed", value: "placed" },
  { label: "Dealer accepted", value: "dealer_accepted" },
  { label: "Accepted", value: "accepted" },
  { label: "Dispatched", value: "dispatched" },
  { label: "Partially Delivered", value: "partially_delivered" },
  { label: "Delivered", value: "delivered" },
  { label: "Rejected", value: "rejected" },
];

export const ORDER_SAMPLE_FILTER_OPTIONS = [
  { label: "All", value: "" },
  { label: "Sample", value: "sample" },
  { label: "Others", value: "main" },
];

export const matchesOrdersSampleFilter = (order, sampleFilter) => {
  if (!sampleFilter) return true;
  const isSample = order?.sample === true;
  if (sampleFilter === "sample") return isSample;
  if (sampleFilter === "main") return !isSample;
  return true;
};

export const ORDERS_SORT_DEFAULT = { key: null, direction: "asc" };

const creditNoteUsageFirst = (row, field) => {
  const projects = row?.usage_projects;
  if (!Array.isArray(projects) || projects.length === 0) return null;
  return projects[0]?.[field] ?? null;
};

export const CREDIT_NOTES_SORT_DEFAULT = {
  key: "identifier",
  direction: "desc",
};

export const sortCreditNotesRows = (rows, sortConfig) => {
  const list = ensureArray(rows);
  if (!sortConfig?.key) return list;

  const sorted = [...list];
  const factor = sortConfig.direction === "asc" ? 1 : -1;

  sorted.sort((a, b) => {
    const key = sortConfig.key;
    if (key === "value" || key === "balance") {
      return ((Number(a?.[key]) || 0) - (Number(b?.[key]) || 0)) * factor;
    }
    if (key === "order_value" || key === "project_credit_total") {
      const pick = (row) => {
        const v = creditNoteUsageFirst(row, key);
        return v == null ? -1 : Number(v);
      };
      return (pick(a) - pick(b)) * factor;
    }
    if (key === "customer") {
      return (
        String(creditNoteUsageFirst(a, "customer_name") || "").localeCompare(
          String(creditNoteUsageFirst(b, "customer_name") || "")
        ) * factor
      );
    }
    if (key === "used_on_order") {
      return (
        String(creditNoteUsageFirst(a, "project_name") || "").localeCompare(
          String(creditNoteUsageFirst(b, "project_name") || "")
        ) * factor
      );
    }
    if (key === "identifier" || key === "status") {
      return (
        String(a?.[key] || "").localeCompare(String(b?.[key] || "")) * factor
      );
    }
    return 0;
  });

  return sorted;
};

const orderMetaTotal = (row, metaKey) => {
  const meta = row?.[metaKey];
  if (!meta || typeof meta !== "object") return 0;
  const n = Number(meta.total);
  return Number.isFinite(n) ? n : 0;
};

export const sortOrdersRows = (rows, sortConfig) => {
  const list = ensureArray(rows);
  if (!sortConfig?.key) return list;

  const sorted = [...list];
  const factor = sortConfig.direction === "asc" ? 1 : -1;
  const key = sortConfig.key === "value" ? "buyer_total" : sortConfig.key;
  sorted.sort((a, b) => {
    if (key === "buyer_total") {
      return (
        (orderMetaTotal(a, "buyer_price_meta") -
          orderMetaTotal(b, "buyer_price_meta")) *
        factor
      );
    }
    if (key === "dealer_total") {
      return (
        (orderMetaTotal(a, "dealer_price_meta") -
          orderMetaTotal(b, "dealer_price_meta")) *
        factor
      );
    }
    if (key === "placed_at" || key === "accepted_at" || key === "rejected_at") {
      return (tsOrZero(a?.[key]) - tsOrZero(b?.[key])) * factor;
    }
    if (key === "delivery_at") {
      return (
        (orderDeliverySortTimestamp(a) - orderDeliverySortTimestamp(b)) * factor
      );
    }
    return (
      String(a?.[key] || "").localeCompare(String(b?.[key] || "")) * factor
    );
  });
  return sorted;
};

export const sortNotificationRows = (rows, sortConfig) => {
  const sorted = [...ensureArray(rows)];
  const factor = sortConfig.direction === "asc" ? 1 : -1;
  sorted.sort((a, b) => {
    const aType = String(a?.recipientType || "");
    const bType = String(b?.recipientType || "");
    if (aType !== bType) {
      if (aType === "buyer") return -1;
      if (bType === "buyer") return 1;
      if (aType === "dealer") return -1;
      if (bType === "dealer") return 1;
    }

    if (sortConfig.key === "name" || sortConfig.key === "templateName") {
      return (
        String(a?.[sortConfig.key] || "").localeCompare(
          String(b?.[sortConfig.key] || "")
        ) * factor
      );
    }
    if (
      sortConfig.key === "createdAt" ||
      sortConfig.key === "statusUpdatedAt"
    ) {
      return (
        (tsOrZero(a?.[sortConfig.key]) - tsOrZero(b?.[sortConfig.key])) * factor
      );
    }
    return 0;
  });

  return sorted;
};

export const sessionCountPhrase = (n) => {
  const num = Number(n || 0);
  return `${num} session${num === 1 ? "" : "s"}`;
};

export const projectCountPhrase = (n) => {
  const num = Number(n || 0);
  return `${num} project${num === 1 ? "" : "s"}`;
};

export const activityCountPhrase = (n) => {
  const num = Number(n || 0);
  return `${num} activit${num === 1 ? "y" : "ies"}`;
};

export function formatMobileDisplay(mobile) {
  const digits = String(mobile ?? "").replace(/\D/g, "");
  const local = digits.length > 10 ? digits.slice(-10) : digits;
  if (local.length !== 10) return String(mobile ?? "").trim();

  return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
}

export function whatsappUrlFromMobile(mobile) {
  const digits = String(mobile ?? "").replace(/\D/g, "");
  if (!digits) return null;

  let national = digits;
  if (digits.length === 12 && digits.startsWith("91")) {
    national = digits.slice(2);
  } else if (digits.length === 11 && digits.startsWith("0")) {
    national = digits.slice(1);
  }

  if (national.length !== 10) return null;

  return `https://wa.me/${national}`;
}

export async function copyTextToClipboard(text) {
  const value = String(text ?? "").trim();
  if (!value) return false;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // fall through to execCommand
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    textarea.remove();
    return copied;
  } catch {
    return false;
  }
}

const INVOICE_DETAILS_CSV_HEADERS = [
  "PROJECT NAME",
  "COMPANY NAME",
  "BUYER NAME",
  "Modular Components Count",
  "Pre tax",
  "Transportation",
  "Total Incl of Tax",
  "ADDRESS",
  "INVOICE NUMBER",
];

const csvEscapeCell = (value) => {
  const str = String(value ?? "");
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const formatInvoiceDetailsBuyerName = (row) => {
  const name = String(row?.buyer_user_name ?? "").trim();
  const mobile = String(row?.buyer_user_mobile ?? "").trim();
  if (name && mobile) return `${name} / ${mobile}`;
  return name || mobile;
};

export const formatInvoiceDetailsAddress = (row) => {
  const parts = [];
  const address = String(row?.delivery_address ?? "").trim();
  if (address) parts.push(address);
  const pincode = String(row?.delivery_pincode ?? "").trim();
  if (pincode) parts.push(`Pincode: ${pincode}`);
  const contact = String(row?.delivery_contact ?? "").trim();
  if (contact) parts.push(contact);
  return parts.join(", ");
};

const invoiceDetailsAmount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return String(n);
};

export const invoiceDetailsPriceFieldsFromMeta = (meta) => {
  if (!meta || typeof meta !== "object") {
    return { preTax: "", transportation: "", totalInclTax: "" };
  }

  const deliveryCharge = Number(meta.delivery_charge) || 0;
  const deliveryGst = Number(meta.delivery_gst) || 0;
  const transportation = deliveryCharge + deliveryGst;

  return {
    preTax: invoiceDetailsAmount(meta.total_after_credit_notes),
    transportation:
      transportation > 0 ? invoiceDetailsAmount(transportation) : "",
    totalInclTax: invoiceDetailsAmount(meta.total),
  };
};

export const buildInvoiceDetailsCsvRows = (orders) =>
  ensureArray(orders).map((row) => {
    const count = row?.modular_components_count;
    const modularCount =
      count == null || count === "" ? "" : String(Number(count) || 0);
    const prices = invoiceDetailsPriceFieldsFromMeta(row?.buyer_price_meta);

    return [
      String(row?.name ?? "").trim(),
      String(row?.buyer_company_name ?? "").trim(),
      formatInvoiceDetailsBuyerName(row),
      modularCount,
      prices.preTax,
      prices.transportation,
      prices.totalInclTax,
      formatInvoiceDetailsAddress(row),
      "",
    ];
  });

export const downloadInvoiceDetailsCsv = (
  orders,
  filename = "invoice_details.csv"
) => {
  const rows = buildInvoiceDetailsCsvRows(orders);
  const lines = [
    INVOICE_DETAILS_CSV_HEADERS.map(csvEscapeCell).join(","),
    ...rows.map((row) => row.map(csvEscapeCell).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

export const STORE_DASHBOARD_TABS = [
  "customers",
  "prospects",
  "leads",
  "contacts",
  "dealers",
  "orders",
  "notifications",
  "coupons",
];

export const STORE_DASHBOARD_BUYER_TABS = [
  "customers",
  "prospects",
  "leads",
  "contacts",
];

export const STORE_DASHBOARD_DEFAULT_TAB = "customers";

export const STORE_DASHBOARD_BUYER_SORT_DEFAULT = {
  key: "lastActivityAt",
  direction: "desc",
};

const STORE_DASHBOARD_VISIBILITY_VALUES = new Set([
  "all",
  "active",
  "sample",
  "ignored",
  "pending",
]);

const STORE_DASHBOARD_MANAGED_URL_KEYS = [
  "tab",
  "search",
  "visibility",
  "ppm",
  "engagement",
  "sales_owner",
  "profile_type",
  "contact_status",
  "source",
  "profile_fit",
  "conviction",
  "committed_date",
  "acquired_month",
  "view",
  "sort",
  "sort_dir",
  ...BUYER_CITY_FILTER_TABS.map((tab) => `city_${tab}`),
  ...BUYER_STATE_FILTER_TABS.map((tab) => `state_${tab}`),
];

export const parseCommaSeparatedUrlList = (value) => {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
};

const serializeCommaSeparatedUrlList = (values) => {
  const list = ensureArray(values)
    .map((value) => String(value).trim())
    .filter(Boolean);
  return list.length > 0 ? list.join(",") : "";
};

export const normalizeStoreSalesDashboardSearchParams = (params) => {
  const entries = [...new URLSearchParams(params).entries()].sort(
    (a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1])
  );
  return new URLSearchParams(entries).toString();
};

export const parseStoreSalesDashboardUrlParams = (searchParams) => {
  const tab = searchParams.get("tab");
  const activeTab = STORE_DASHBOARD_TABS.includes(tab)
    ? tab
    : STORE_DASHBOARD_DEFAULT_TAB;

  const buyerCityFiltersByTab = BUYER_CITY_FILTER_TABS.reduce((acc, cityTab) => {
    acc[cityTab] = parseCommaSeparatedUrlList(
      searchParams.get(`city_${cityTab}`)
    );
    return acc;
  }, {});

  const buyerStateFiltersByTab = BUYER_STATE_FILTER_TABS.reduce((acc, stateTab) => {
    acc[stateTab] = parseCommaSeparatedUrlList(
      searchParams.get(`state_${stateTab}`)
    );
    return acc;
  }, {});

  const visibility = String(searchParams.get("visibility") || "all").trim();
  const buyerVisibilityFilter = STORE_DASHBOARD_VISIBILITY_VALUES.has(visibility)
    ? visibility
    : "all";

  const sortKey =
    searchParams.get("sort") || STORE_DASHBOARD_BUYER_SORT_DEFAULT.key;
  const sortDirection = searchParams.has("sort_dir")
    ? searchParams.get("sort_dir") === "asc"
      ? "asc"
      : "desc"
    : STORE_DASHBOARD_BUYER_SORT_DEFAULT.direction;

  return {
    activeTab,
    searchText: searchParams.get("search") || "",
    buyerVisibilityFilter,
    buyerCityFiltersByTab,
    buyerStateFiltersByTab,
    buyerPpmFilter: parseCommaSeparatedUrlList(searchParams.get("ppm")),
    buyerEngagementFilter: parseCommaSeparatedUrlList(
      searchParams.get("engagement")
    ),
    buyerSalesOwnerFilter: parseCommaSeparatedUrlList(
      searchParams.get("sales_owner")
    ),
    buyerProfileTypeFilter: parseCommaSeparatedUrlList(
      searchParams.get("profile_type")
    ),
    buyerContactStatusFilter: searchParams.get("contact_status") || "",
    buyerSourceFilter: searchParams.get("source") || "",
    profileFitFilter: (() => {
      const raw = searchParams.get("profile_fit");
      if (!raw) return [];
      if (raw === "all") return [];
      if (raw === "1") return ["fit"];
      return parseCommaSeparatedUrlList(raw).filter((value) =>
        STORE_BUYER_FIT_VALUES.includes(value)
      );
    })(),
    convictionFilter: parseCommaSeparatedUrlList(
      searchParams.get("conviction")
    ).filter((value) => STORE_BUYER_CONVICTION_VALUES.includes(value)),
    committedDateFilter: parseCommittedDateFilterFromUrl(
      searchParams.get("committed_date")
    ),
    buyerAcquiredMonthFilter: parseCommaSeparatedUrlList(
      searchParams.get("acquired_month")
    ),
    buyersTableViewMode: (() => {
      const view = String(searchParams.get("view") || "").trim();
      if (view === "wide") return "wide";
      if (view === "remarks") return "remarks";
      return "compact";
    })(),
    sortConfig: {
      key: sortKey,
      direction: sortDirection,
    },
  };
};

export const buildStoreSalesDashboardSearchParams = (state, existingParams) => {
  const params = new URLSearchParams(existingParams || undefined);

  STORE_DASHBOARD_MANAGED_URL_KEYS.forEach((key) => params.delete(key));

  const activeTab = state.activeTab || STORE_DASHBOARD_DEFAULT_TAB;
  if (activeTab !== STORE_DASHBOARD_DEFAULT_TAB) {
    params.set("tab", activeTab);
  }

  if (!STORE_DASHBOARD_BUYER_TABS.includes(activeTab)) {
    return params;
  }

  const searchText = String(state.searchText || "").trim();
  if (searchText) {
    params.set("search", searchText);
  }

  const visibility = String(state.buyerVisibilityFilter || "all").trim();
  if (visibility && visibility !== "all") {
    params.set("visibility", visibility);
  }

  BUYER_CITY_FILTER_TABS.forEach((cityTab) => {
    const serialized = serializeCommaSeparatedUrlList(
      state.buyerCityFiltersByTab?.[cityTab]
    );
    if (serialized) {
      params.set(`city_${cityTab}`, serialized);
    }
  });

  BUYER_STATE_FILTER_TABS.forEach((stateTab) => {
    const serialized = serializeCommaSeparatedUrlList(
      state.buyerStateFiltersByTab?.[stateTab]
    );
    if (serialized) {
      params.set(`state_${stateTab}`, serialized);
    }
  });

  const ppm = serializeCommaSeparatedUrlList(state.buyerPpmFilter);
  if (ppm) params.set("ppm", ppm);

  const engagement = serializeCommaSeparatedUrlList(state.buyerEngagementFilter);
  if (engagement) params.set("engagement", engagement);

  const salesOwner = serializeCommaSeparatedUrlList(
    state.buyerSalesOwnerFilter
  );
  if (salesOwner) params.set("sales_owner", salesOwner);

  const profileType = serializeCommaSeparatedUrlList(
    state.buyerProfileTypeFilter
  );
  if (profileType) params.set("profile_type", profileType);

  const contactStatus = String(state.buyerContactStatusFilter || "").trim();
  if (contactStatus) params.set("contact_status", contactStatus);

  const source = String(state.buyerSourceFilter || "").trim();
  if (source) params.set("source", source);

  const profileFit = serializeCommaSeparatedUrlList(state.profileFitFilter);
  if (profileFit) params.set("profile_fit", profileFit);

  const conviction = serializeCommaSeparatedUrlList(state.convictionFilter);
  if (conviction) params.set("conviction", conviction);

  const committedDate = serializeCommaSeparatedUrlList(
    state.committedDateFilter
  );
  if (committedDate) params.set("committed_date", committedDate);

  if (activeTab === "customers") {
    const acquiredMonth = serializeCommaSeparatedUrlList(
      state.buyerAcquiredMonthFilter
    );
    if (acquiredMonth) params.set("acquired_month", acquiredMonth);
  }

  const viewMode = String(state.buyersTableViewMode || "compact").trim();
  if (viewMode === "wide" || viewMode === "remarks") {
    params.set("view", viewMode);
  }

  const sortConfig = state.sortConfig || STORE_DASHBOARD_BUYER_SORT_DEFAULT;
  const sortKey = String(sortConfig.key || "").trim();
  const sortDirection = sortConfig.direction === "asc" ? "asc" : "desc";
  if (
    sortKey &&
    (sortKey !== STORE_DASHBOARD_BUYER_SORT_DEFAULT.key ||
      sortDirection !== STORE_DASHBOARD_BUYER_SORT_DEFAULT.direction)
  ) {
    params.set("sort", sortKey);
    if (sortDirection !== STORE_DASHBOARD_BUYER_SORT_DEFAULT.direction) {
      params.set("sort_dir", sortDirection);
    }
  }

  return params;
};
